import { FrequencyRecord, GAME_MODE, NotesSection, PlayerNote, SongTrack, songBeat } from '~/interfaces';
import { GameStateClass } from '~/modules/GameEngine/GameState/GameState';
import { appendFrequencyToPlayerNotes } from '~/modules/GameEngine/GameState/Helpers/appendFrequencyToPlayerNotes';
import calculateScore, { MAX_POINTS, sumDetailedScore } from '~/modules/GameEngine/GameState/Helpers/calculateScore';
import { calculateDetailedScoreData } from '~/modules/GameEngine/GameState/Helpers/calculateScore';
import InputManager from '~/modules/GameEngine/Input/InputManager';
import events from '~/modules/GameEvents/GameEvents';
import isNotesSection from '~/modules/Songs/utils/isNotesSection';
import { getNoteAtBeat } from '~/modules/Songs/utils/notesSelectors';
import getSongBeatCount from '~/modules/Songs/utils/getSongBeatCount';

type DetailedScore = {
  freestyle: number;
  rap: number;
  rapstar: number;
  star: number;
  normal: number;
  perfect: number;
  vibrato: number;
};

const ZERO_DETAILED: DetailedScore = {
  freestyle: 0,
  rap: 0,
  rapstar: 0,
  star: 0,
  normal: 0,
  perfect: 0,
  vibrato: 0,
};

// ---- peque PRNG deterministic (para bonus "realistas" pero estables) ----
function hashStringToInt(str: string) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
function xorshift32(state: number) {
  let x = state | 0;
  x ^= x << 13;
  x ^= x >>> 17;
  x ^= x << 5;
  return x >>> 0;
}
function rand01(stateRef: { v: number }) {
  stateRef.v = xorshift32(stateRef.v);
  return (stateRef.v & 0xfffffff) / 0xfffffff;
}

class PlayerState {
  private frequencyRecords: FrequencyRecord[] = [];
  private playerNotes: PlayerNote[] = [];
  private realFrequencyRecords: FrequencyRecord[] = [];
  private realPlayerNotes: PlayerNote[] = [];
  private min = Infinity;
  private max = -Infinity;

  private storedSectionIndex = 0;

  // ✅ Fallback scoring state
  private hasScoringNotes = true;
  private lastFallbackTs: number | null = null;
  private fallbackActiveMs = 0; // ms cantando (freq > 0)
  private fallbackTotalMs = 0; // ms totales procesados
  private fallbackCountsBeats: DetailedScore = { ...ZERO_DETAILED }; // OJO: en "beats-space" (no puntos)
  private rng = { v: 1 };

  public constructor(
    private number: 0 | 1 | 2 | 3,
    private gameState: GameStateClass,
  ) {
    const track = this.getTrack();
    const notesSections = track.sections.filter(isNotesSection);
    const notesCount = notesSections.reduce((acc, s) => acc + s.notes.length, 0);

    const song = this.gameState.getSong();
    const isTemplateFallback = !!song?.meta?.isTemplateFallback;
    this.hasScoringNotes = notesCount > 0 && !isTemplateFallback;

    // init rng estable por canción+player
    const songId = this.gameState.getSong()?.id ?? 'unknown-song';
    this.rng.v = hashStringToInt(`${songId}:${this.number}`);

    // min/max pitch (solo si hay notas)
    notesSections.forEach((section) =>
      section.notes.forEach((note) => {
        this.min = Math.min(this.min, note.pitch);
        this.max = Math.max(this.max, note.pitch);
      }),
    );
  }

  public getNumber = () => this.number;

  public update = () => {
    const currentTime = this.gameState.getCurrentTime();
    const frequency = InputManager.getPlayerFrequency(this.number);
    const currentTimestamp = currentTime - InputManager.getPlayerInputLag(this.number);

    // If it's a pack of frequencies (from remote mic), restore last "real" frequencies,
    // add and recalculate for the received pack, and store newly computed
    // frequencies for the moment when new package arrive.
    // If it's a single frequency, just add it to the notes
    if (Array.isArray(frequency)) {
      const lastRealRecord = this.realFrequencyRecords.at(-1) || this.frequencyRecords.at(0);
      const lastTimestamp = lastRealRecord?.timestamp ?? 0;
      const timestampStep = (currentTimestamp - lastTimestamp) / Math.max(1, frequency.length);

      this.frequencyRecords = this.realFrequencyRecords;
      this.playerNotes = this.realPlayerNotes;

      for (let i = 0; i < frequency.length; i++) {
        const timestamp = lastTimestamp + timestampStep * (i + 1);

        this.updateScoring(timestamp, frequency[i]);
      }

      this.realFrequencyRecords = [...this.frequencyRecords];
      this.realPlayerNotes = [...this.playerNotes];

      const lastNote = this.realPlayerNotes.at(-1);
      if (lastNote) {
        this.realPlayerNotes[this.realPlayerNotes.length - 1] = {
          ...lastNote,
          frequencyRecords: [...(lastNote.frequencyRecords ?? [])],
        };
      }
      InputManager.clearPlayerCachedFrequencies(this.number);
    } else {
      // single frequency
      this.updateScoring(currentTimestamp, frequency);
    }

    this.dispatchSectionUpdate();
  };

  private dispatchSectionUpdate = () => {
    const currentSectionIndex = this.getCurrentSectionIndex();
    if (this.storedSectionIndex !== currentSectionIndex) {
      events.sectionChange.dispatch(this.number, this.storedSectionIndex);
      this.storedSectionIndex = currentSectionIndex;
    }
  };

  private getSectionIndexByTrackBeat = (track: SongTrack, beat: songBeat) => {
    return track.sections.findIndex((section, index, sections) => {
      if (beat < 0) return true;
      if (beat < section.start) return false;
      if (index === sections.length - 1) return true;
      return sections[index + 1].start > beat;
    });
  };

  private getSectionByTrackBeat = (track: SongTrack, beat: songBeat) =>
    track.sections[this.getSectionIndexByTrackBeat(track, beat)] ?? null;

  private getTrackScoringOffsetMs = (trackIndex = this.getTrackIndex()) => {
    const offset = Number(this.gameState.getSong()?.trackOffsetsMs?.[trackIndex] ?? 0);
    return Number.isFinite(offset) ? offset : 0;
  };

  private getScoringTimestampForTrack = (timestamp: number, trackIndex = this.getTrackIndex()) =>
    timestamp - this.getTrackScoringOffsetMs(trackIndex);

  private getScoringBeatForTrack = (timestamp: number, trackIndex = this.getTrackIndex()) => {
    const beatLength = this.gameState.getSongBeatLength();
    return beatLength > 0 ? this.getScoringTimestampForTrack(timestamp, trackIndex) / beatLength : 0;
  };

  private trackHasNoteAtBeat = (track: SongTrack, beat: songBeat) => {
    const section = this.getSectionByTrackBeat(track, beat);
    if (!section || !isNotesSection(section)) return false;

    return Boolean(getNoteAtBeat(section, beat, 0) ?? getNoteAtBeat(section, beat, 0.5));
  };

  private getTrackActivePhraseAtBeat = (track: SongTrack, beat: songBeat) => {
    const section = this.getSectionByTrackBeat(track, beat);
    if (!section || !isNotesSection(section) || section.notes.length === 0) return null;

    const firstNote = section.notes[0];
    const lastNote = section.notes.at(-1)!;
    const beatLength = this.gameState.getSongBeatLength();
    const toleranceBeats = beatLength > 0 ? Math.min(2, Math.max(0.5, 250 / beatLength)) : 0.5;

    if (beat < firstNote.start - toleranceBeats || beat > lastNote.start + lastNote.length + toleranceBeats) {
      return null;
    }

    return {
      start: firstNote.start,
      hasNote: this.trackHasNoteAtBeat(track, beat),
    };
  };

  private getPassTheMicMergedTrackPlayerNumber = (timestamp: number) => {
    const song = this.gameState.getSong();
    const setup = this.gameState.getSingSetup();
    const beatLength = this.gameState.getSongBeatLength();
    const beat = beatLength > 0 ? timestamp / beatLength : 0;

    if (!setup?.players.length) return this.number;

    const changes = song?.mergedTrack?.changes ?? [];
    const segmentIndex = changes.findIndex((changeBeat) => beat < changeBeat);
    const candidateIndex = (segmentIndex === -1 ? changes.length : segmentIndex) % setup.players.length;

    return setup.players[candidateIndex]?.number ?? this.number;
  };

  private getPassTheMicActivePlayerNumber = (timestamp: number) => {
    const song = this.gameState.getSong();
    const setup = this.gameState.getSingSetup();
    const beatLength = this.gameState.getSongBeatLength();
    const beat = beatLength > 0 ? timestamp / beatLength : 0;

    if (!song || !setup?.players.length) return this.number;

    if (this.gameState.isMergedTrack() || song.tracks.length < 2) {
      return this.getPassTheMicMergedTrackPlayerNumber(timestamp);
    }

    const activeCandidates = setup.players.flatMap((player) => {
      const track = song.tracks[player.track];
      if (!track) return [];

      const hasTrackOffset = this.getTrackScoringOffsetMs(player.track) !== 0;
      const playerBeat = hasTrackOffset ? this.getScoringBeatForTrack(timestamp, player.track) : beat;
      const phrase = this.getTrackActivePhraseAtBeat(track, playerBeat);

      return phrase
        ? [
            {
              player,
              hasTrackOffset,
              ...phrase,
            },
          ]
        : [];
    });

    if (activeCandidates.length > 0) {
      const candidatesWithNotes = activeCandidates.filter((candidate) => candidate.hasNote);
      const baseTimelineCandidates = activeCandidates.filter((candidate) => !candidate.hasTrackOffset);
      const candidates =
        candidatesWithNotes.length > 0
          ? candidatesWithNotes
          : baseTimelineCandidates.length > 0
            ? baseTimelineCandidates
            : activeCandidates;

      return [...candidates].sort((a, b) =>
        b.start - a.start || b.player.track - a.player.track || b.player.number - a.player.number
      )[0].player.number;
    }

    const changes = song.mergedTrack?.changes ?? [];
    const segmentIndex = changes.findIndex((changeBeat) => beat < changeBeat);
    const candidateIndex = (segmentIndex === -1 ? changes.length : segmentIndex) % setup.players.length;

    return setup.players[candidateIndex]?.number ?? this.number;
  };

  private shouldProcessScoringAtTimestamp = (timestamp: number) => {
    const setup = this.gameState.getSingSetup();
    if (setup?.mode !== GAME_MODE.PASS_THE_MIC || setup.players.length < 2) return true;

    return this.getPassTheMicActivePlayerNumber(timestamp) === this.number;
  };

  private recordInactiveFrequency = (timestamp: number, frequency: number) => {
    this.frequencyRecords.push({ timestamp, frequency });
    if (!this.hasScoringNotes) this.lastFallbackTs = timestamp;
  };

  private updateScoring = (timestamp: number, frequency: number) => {
    if (!this.shouldProcessScoringAtTimestamp(timestamp)) {
      this.recordInactiveFrequency(timestamp, frequency);
      return;
    }

    if (this.hasScoringNotes) {
      this.updatePlayerNotes(timestamp, frequency);
    } else {
      this.updateFallbackScoring(timestamp, frequency);
    }
  };

  public updatePlayerNotes = (timestamp: number, frequency: number) => {
    const scoringTimestamp = this.getScoringTimestampForTrack(timestamp);
    const record = { timestamp: scoringTimestamp, frequency };
    this.frequencyRecords.push(record);

    const recordBeat = record.timestamp / this.gameState.getSongBeatLength();
    const recordSection = this.getSectionByBeat(recordBeat);

    if (isNotesSection(recordSection)) {
      const note = getNoteAtBeat(recordSection, recordBeat, 0) ?? getNoteAtBeat(recordSection, recordBeat, 0.5);
      if (note) appendFrequencyToPlayerNotes(this.playerNotes, record, note, this.gameState.getSongBeatLength());
    }
  };

  // ✅ Fallback scoring: crea puntuación "creíble" sin notas
  private updateFallbackScoring = (timestamp: number, frequency: number) => {
    const record = { timestamp, frequency };
    this.frequencyRecords.push(record);

    if (this.lastFallbackTs === null) {
      this.lastFallbackTs = timestamp;
      return;
    }

    let dt = timestamp - this.lastFallbackTs;
    this.lastFallbackTs = timestamp;

    // clamps para evitar saltos raros
    if (!Number.isFinite(dt) || dt < 0) return;
    if (dt > 1500) dt = 1500;

    this.fallbackTotalMs += dt;

    // Heurística simple: si hay frecuencia (>0) asumimos "sonido/voz"
    const isActive = frequency > 0;

    if (isActive) this.fallbackActiveMs += dt;

    const beatLen = this.gameState.getSongBeatLength();
    const dtBeats = beatLen > 0 ? dt / beatLen : 0;

    // Base: acumula "normal" y "freestyle" mientras haya actividad
    if (isActive && dtBeats > 0) {
      // Normal sube más: la demo se siente "karaoke real"
      this.fallbackCountsBeats.normal += dtBeats * 1.0;
      this.fallbackCountsBeats.freestyle += dtBeats * 0.35;

      // Perfect depende de "consistencia" (actividad sostenida)
      const activityRatio = this.fallbackActiveMs / Math.max(1, this.fallbackTotalMs);
      this.fallbackCountsBeats.perfect += dtBeats * (0.25 + 0.35 * activityRatio);

      // Bonus ocasionales pero “creíbles”
      const r = rand01(this.rng);

      // Star: rara (picos)
      if (r < 0.015) {
        this.fallbackCountsBeats.star += dtBeats * 0.9; // pequeño “boost”
      }

      // Vibrato: algo más frecuente que star
      if (r > 0.7 && r < 0.75) {
        this.fallbackCountsBeats.vibrato += dtBeats * 0.6;
      }
    } else {
      // Sin actividad: si quieres, puedes dar un goteo mínimo o 0
      // (lo dejo casi 0 para que motive a “cantar”)
      this.fallbackCountsBeats.freestyle += dtBeats * 0.02;
    }

    // Limita para que no crezca infinito si el vídeo se pausa/buggea
    const totalBeats = getSongBeatCount(this.gameState.getSong()!);
    const cap = Math.max(1, totalBeats);

    // caps suaves (para no superar el máximo teórico)
    this.fallbackCountsBeats.normal = Math.min(this.fallbackCountsBeats.normal, cap);
    this.fallbackCountsBeats.freestyle = Math.min(this.fallbackCountsBeats.freestyle, cap * 0.6);
    this.fallbackCountsBeats.perfect = Math.min(this.fallbackCountsBeats.perfect, cap * 0.75);
    this.fallbackCountsBeats.star = Math.min(this.fallbackCountsBeats.star, cap * 0.25);
    this.fallbackCountsBeats.vibrato = Math.min(this.fallbackCountsBeats.vibrato, cap * 0.35);
  };

  public getPlayerNotes = () => this.playerNotes;
  public getPlayerFrequencies = () => this.frequencyRecords;

  public getSectionIndexByBeat = (beat: songBeat) => {
    return this.getTrack().sections.findIndex((section, index, sections) => {
      if (beat < 0) return true;
      if (beat < section.start) return false;
      if (index === sections.length - 1) return true;
      return sections[index + 1].start > beat;
    });
  };

  public getNoteAtBeat = (beat: songBeat) => {
    const section = this.getSectionByBeat(beat);
    if (!section || !isNotesSection(section)) return undefined;
    return getNoteAtBeat(section, beat, 0);
  };

  public getCurrentSectionIndex = () => this.getSectionIndexByBeat(this.gameState.getCurrentBeat());
  public getCurrentNote = () => this.getNoteAtBeat(this.gameState.getCurrentBeat());

  public getSectionByBeat = (beat: number) => this.getTrack().sections[this.getSectionIndexByBeat(beat)] ?? null;

  public getCurrentSection = () => {
    const sectionIndex = this.getCurrentSectionIndex();
    return this.getTrack().sections[sectionIndex] ?? null;
  };

  public getNextSection = (index = 1) => {
    const sectionIndex = this.getCurrentSectionIndex();
    return sectionIndex > -1 ? (this.getTrack().sections[sectionIndex + index] ?? null) : null;
  };

  public getPreviousSection = (index = 1) => {
    const sectionIndex = this.getCurrentSectionIndex();
    return sectionIndex > -1 ? (this.getTrack().sections[sectionIndex - index] ?? null) : null;
  };

  public getLastNotesSection = () => {
    const last = this.getTrack().sections.at(-1);
    return isNotesSection(last) ? last : (this.getTrack().sections.at(-2) as NotesSection);
  };

  public getScore = () => {
    // ✅ scoring real si hay notas
    if (this.hasScoringNotes) {
      return calculateScore(this.playerNotes, this.gameState.getSong()!, this.getTrackIndex());
    }

    // ✅ scoring fallback: usa su detailedScore “beats-space”
    const [ppb, counts] = this.getDetailedScore();
    return sumDetailedScore(counts) * ppb;
  };

  public getDetailedScore = () => {
    // ✅ scoring real si hay notas
    if (this.hasScoringNotes) {
      return calculateDetailedScoreData(this.playerNotes, this.gameState.getSong()!, this.getTrackIndex());
    }

    // ✅ scoring fallback
    const song = this.gameState.getSong()!;
    const totalBeats = Math.max(1, getSongBeatCount(song));

    // Definimos un "máximo teórico" en beats-space (coherente con sumDetailedScore)
    const maxCounts: DetailedScore = {
      // freestyle suma menos en la demo (para que la barra “normal” parezca principal)
      freestyle: totalBeats * 0.35,
      rap: 0,
      rapstar: 0,
      star: totalBeats * 0.25,
      normal: totalBeats * 1.0,
      perfect: totalBeats * 0.75,
      vibrato: totalBeats * 0.35,
    };

    const maxSum = sumDetailedScore(maxCounts);
    const pointsPerBeat = maxSum > 0 ? MAX_POINTS / maxSum : 0;

    // counts ya los estamos guardando en beats-space en updateFallbackScoring()
    const counts: DetailedScore = { ...ZERO_DETAILED, ...this.fallbackCountsBeats };

    // por si acaso, evitar NaN
    if (!Number.isFinite(pointsPerBeat)) {
      return [0, ZERO_DETAILED, maxCounts] as const;
    }

    return [pointsPerBeat, counts, maxCounts] as const;
  };

  public getMinPitch = () => this.min;
  public getMaxPitch = () => this.max;

  public getTrackIndex = () =>
    this.gameState.getSingSetup()!.players.find((player) => player.number === this.number)!.track;

  public getTrack = () => {
    const song = this.gameState.getSong()!;

    if (this.gameState.isMergedTrack()) {
      return song.mergedTrack;
    } else {
      return song.tracks[this.getTrackIndex()];
    }
  };

  public resetNotes = () => {
    this.playerNotes = [];
    this.frequencyRecords = [];
    this.realPlayerNotes = [];
    this.realFrequencyRecords = [];

    // reset fallback scoring too
    this.lastFallbackTs = null;
    this.fallbackActiveMs = 0;
    this.fallbackTotalMs = 0;
    this.fallbackCountsBeats = { ...ZERO_DETAILED };
  };
}

export default PlayerState;
